import { useState, useRef, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { useTranslation } from "react-i18next";
import { SUPPORTED_LANGUAGES } from "../i18n";
import {
  LayoutDashboard, AlertTriangle, ClipboardCheck, FileText,
  GraduationCap, Truck, AlertCircle, BarChart3, Users, FolderKanban,
  LogOut, Menu, X, ChevronDown, User, Sparkles, BookOpen, Calculator,
  Workflow, FileQuestion, PieChart, GanttChart, Search, ShieldCheck, FileCheck, FolderOpen, Bell, Grid3X3,
  TrendingUp, TrendingDown, Cog, Activity, Network, Lightbulb, Siren, FileUp, Shield, Key,
  Brain, Cpu, Store, Gauge, Globe, Radar, HelpCircle, FlaskConical, Database, PenTool, QrCode, Image, Layers, ShieldAlert,
  ArrowLeft, Home, Building2, TicketCheck, Headphones, Banknote, HeartPulse
} from "lucide-react";
import AiNoteTaker from "./AiNoteTaker";
import NotificationBell from "./NotificationBell";
import ContextBreadcrumb from "./ContextBreadcrumb";
import CommandPalette from "./CommandPalette";
import CommandPaletteLauncher from "./CommandPaletteLauncher";
import Sidebar from "./Sidebar";
import { CommandPaletteProvider } from "../contexts/CommandPaletteContext";

// PR_RUN_039 — localStorage key + hydrator moved into Sidebar.jsx along
// with the rest of the sidebar-internal state.

const QMSLayout = ({ children, title }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const { t, i18n } = useTranslation(['nav', 'common']);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [langFocusIndex, setLangFocusIndex] = useState(-1);
  const langBtnRef = useRef(null);
  const langListRef = useRef(null);
  const langOptionRefs = useRef([]);

  // Breadcrumb map — client-side path hierarchy
  const BREADCRUMB_MAP = {
    "/dashboard": [{ label: "Dashboard", path: "/dashboard" }],
    "/governance": [{ label: "Dashboard", path: "/dashboard" }, { label: "Governance", path: "/governance" }],
    "/governance/standards": [{ label: "Dashboard", path: "/dashboard" }, { label: "Governance", path: "/governance" }, { label: "Standards" }],
    "/governance/soa": [{ label: "Dashboard", path: "/dashboard" }, { label: "Governance", path: "/governance" }, { label: "SoA" }],
    "/audit-conductor": [{ label: "Dashboard", path: "/dashboard" }, { label: "Audit Conductor" }],
    "/scriber": [{ label: "Dashboard", path: "/dashboard" }, { label: "SCRIBER" }],
    "/capa": [{ label: "Dashboard", path: "/dashboard" }, { label: "CAPA" }],
    "/audits": [{ label: "Dashboard", path: "/dashboard" }, { label: "Audits" }],
    "/reports": [{ label: "Dashboard", path: "/dashboard" }, { label: "Reports" }],
    "/crm/organizations": [{ label: "Dashboard", path: "/dashboard" }, { label: "CRM", path: "/crm/organizations" }, { label: "Organizations" }],
    "/crm/support-tickets": [{ label: "Dashboard", path: "/dashboard" }, { label: "CRM", path: "/crm/organizations" }, { label: "Support Tickets" }],
    "/my-support": [{ label: "Dashboard", path: "/dashboard" }, { label: "My Support" }],
    "/client-onboarding": [{ label: "Dashboard", path: "/dashboard" }, { label: "My Onboarding" }],
    "/client-portal": [{ label: "Dashboard", path: "/dashboard" }, { label: "Client Portal" }],
    "/documents": [{ label: "Dashboard", path: "/dashboard" }, { label: "Documents" }],
    "/qa-supervisor": [{ label: "Dashboard", path: "/dashboard" }, { label: "QA Supervisor" }],
    "/notification-log": [{ label: "Dashboard", path: "/dashboard" }, { label: "Notifications" }],
    "/notification-settings": [{ label: "Dashboard", path: "/dashboard" }, { label: "Admin", path: "/users" }, { label: "Notification Settings" }],
    "/training": [{ label: "Dashboard", path: "/dashboard" }, { label: "Training" }],
    "/suppliers": [{ label: "Dashboard", path: "/dashboard" }, { label: "Suppliers" }],
    "/compliance": [{ label: "Dashboard", path: "/dashboard" }, { label: "Compliance Navigator" }],
    "/compliance-dashboard": [{ label: "Dashboard", path: "/dashboard" }, { label: "Compliance Dashboard" }],
    "/users": [{ label: "Dashboard", path: "/dashboard" }, { label: "Admin", path: "/users" }, { label: "Users" }],
    "/regional-obligations": [{ label: "Dashboard", path: "/dashboard" }, { label: "Governance", path: "/governance" }, { label: "Regional Obligations" }],
    "/war-room": [{ label: "Dashboard", path: "/dashboard" }, { label: "War Room" }],
    "/findings": [{ label: "Dashboard", path: "/dashboard" }, { label: "Findings" }],
    "/deviations": [{ label: "Dashboard", path: "/dashboard" }, { label: "Deviations" }],
    "/communications": [{ label: "Dashboard", path: "/dashboard" }, { label: "Communications" }],
    "/contacts": [{ label: "Dashboard", path: "/dashboard" }, { label: "Contacts" }],
    "/messages": [{ label: "Dashboard", path: "/dashboard" }, { label: "Messages" }],
    "/predictive-intelligence": [{ label: "Dashboard", path: "/dashboard" }, { label: "Predictive Intelligence" }],
    "/annex11-readiness": [{ label: "Dashboard", path: "/dashboard" }, { label: "Governance", path: "/governance" }, { label: "Annex 11 Readiness" }],
    "/doc-control": [{ label: "Dashboard", path: "/dashboard" }, { label: "Document Control" }],
    "/svr": [{ label: "Dashboard", path: "/dashboard" }, { label: "Software Validation Reports" }],
    "/iso9001": [{ label: "Dashboard", path: "/dashboard" }, { label: "ISO 9001 Self-Assessment" }],
  };

  // Resolve breadcrumb for current path (exact match, then prefix match)
  const currentPath = location.pathname;
  let breadcrumbItems = BREADCRUMB_MAP[currentPath];
  if (!breadcrumbItems) {
    for (const key of Object.keys(BREADCRUMB_MAP).sort((a, b) => b.length - a.length)) {
      if (currentPath.startsWith(key) && key !== "/dashboard") {
        breadcrumbItems = [...BREADCRUMB_MAP[key]];
        // Add the dynamic segment as last crumb
        const suffix = currentPath.slice(key.length).replace(/^\//, "");
        if (suffix) breadcrumbItems.push({ label: suffix.split("/")[0] });
        break;
      }
    }
  }

  // Language switcher handler
  const changeLang = (lng) => {
    i18n.changeLanguage(lng);
    setLangOpen(false);
    // PR_RUN_044 — sidebar language dropdown now closes itself inside
    // Sidebar.jsx (line where `changeLang(lang.code)` fires, followed by
    // `setSidebarLangOpen(false)`). The stale parent-scope call to
    // `setSidebarLangOpen(false)` is removed to fix the ReferenceError.
    langBtnRef.current?.focus();
  };

  // Toggle the topbar language dropdown with focus management
  const toggleLangDropdown = useCallback((open) => {
    setLangOpen(open);
    if (open) {
      const activeIdx = SUPPORTED_LANGUAGES.findIndex(l => l.code === i18n.language);
      setLangFocusIndex(activeIdx >= 0 ? activeIdx : 0);
      // Focus the active option after render
      setTimeout(() => langOptionRefs.current[activeIdx >= 0 ? activeIdx : 0]?.focus(), 0);
    } else {
      setLangFocusIndex(-1);
      langBtnRef.current?.focus();
    }
  }, [i18n.language]);

  // Keyboard handler for the language dropdown listbox
  const handleLangListKeyDown = useCallback((e) => {
    const count = SUPPORTED_LANGUAGES.length;
    if (e.key === 'Escape') {
      e.preventDefault();
      toggleLangDropdown(false);
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = (langFocusIndex + 1) % count;
      setLangFocusIndex(next);
      langOptionRefs.current[next]?.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const prev = (langFocusIndex - 1 + count) % count;
      setLangFocusIndex(prev);
      langOptionRefs.current[prev]?.focus();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (langFocusIndex >= 0 && langFocusIndex < count) {
        changeLang(SUPPORTED_LANGUAGES[langFocusIndex].code);
      }
    } else if (e.key === 'Tab') {
      toggleLangDropdown(false);
    }
  }, [langFocusIndex, toggleLangDropdown]);

  // Keyboard handler for the trigger button
  const handleLangBtnKeyDown = useCallback((e) => {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      if (!langOpen) toggleLangDropdown(true);
    }
  }, [langOpen, toggleLangDropdown]);

  // Get current language info
  const currentLang = SUPPORTED_LANGUAGES.find(l => l.code === i18n.language) || SUPPORTED_LANGUAGES[0];

  const contextMap = {
    "/dashboard": { context: null, label: "Dashboard" },
    "/capa": { context: "capa", label: "CAPA" },
    "/audits": { context: "audit", label: "Audits" },
    "/deviations": { context: "deviation", label: "Deviations" },
    "/documents": { context: "document", label: "Documents" },
    "/training": { context: "training", label: "Training" },
    "/suppliers": { context: "supplier", label: "Suppliers" },
    "/reports": { context: null, label: "Reports" },
    "/knowledge": { context: "quality", label: "Knowledge" },
    "/calculators": { context: "quality", label: "Calculators" },
    "/audit-workflow": { context: "audit", label: "Audit Workflow" },
    "/questionnaire": { context: null, label: "Questionnaire" },
    "/client-portal": { context: null, label: "Client Portal" },
    "/client-onboarding": { context: null, label: "Client Onboarding" },
    "/notifications": { context: null, label: "Notifications" },
    "/qa-supervisor": { context: null, label: "QA Supervisor" },
    "/gantt": { context: null, label: "Gantt Chart" },
    "/gap-assessment": { context: "quality", label: "Gap Assessment" },
    "/audit-readiness": { context: "audit", label: "Audit Readiness" },
    "/project-closure": { context: null, label: "Project Closure" },
    "/client-documents": { context: "document", label: "Client Documents" },
    "/audit-matrices": { context: "quality", label: "Audit Matrices" },
    "/trend-analysis": { context: "quality", label: "Trend Analysis" },
    "/process-capture": { context: "quality", label: "Process Capture" },
    "/notification-settings": { context: null, label: "Notification Settings" },
    "/predictive-intelligence": { context: "quality", label: "Predictive Intelligence" },
    "/annex11-readiness": { context: "governance", label: "Annex 11 Readiness" },
    "/doc-control": { context: "document", label: "Document Control" },
    "/svr": { context: "document", label: "Software Validation Reports" },
    "/iso9001": { context: "governance", label: "ISO 9001 Self-Assessment" },
  };
  const currentCtx = contextMap[location.pathname] || { context: null, label: title };

  const navGroups = [
    {
      id: "operations",
      label: t("nav:groups.operations"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/dashboard", label: t("nav:items.dashboard"), icon: LayoutDashboard },
        { path: "/capa", label: t("nav:items.capa"), icon: AlertTriangle },
        { path: "/audits", label: t("nav:items.audits"), icon: ClipboardCheck },
        { path: "/deviations", label: t("nav:items.deviations"), icon: AlertCircle },
        { path: "/trend-analysis", label: t("nav:items.trendAnalysis"), icon: TrendingUp, highlight: true },
      ],
    },
    {
      id: "predictive",
      label: t("nav:groups.predictive"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/predictive-intelligence", label: t("nav:items.predictiveIntelligence"), icon: Brain, highlight: true },
      ],
    },
    {
      id: "agentic",
      label: t("nav:groups.agentic"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/gap-assessment", label: t("nav:items.gapAssessment"), icon: Search, highlight: true },
        { path: "/audit-readiness", label: t("nav:items.auditReadiness"), icon: ShieldCheck, highlight: true },
        { path: "/quality-agreement", label: t("nav:items.qmsToolkit"), icon: FileCheck, highlight: true },
        { path: "/competitor-analysis", label: t("nav:items.marketIntel"), icon: PieChart, highlight: true, roles: ["admin"] },
        { path: "/project-closure", label: t("nav:items.closureApprovals"), icon: FileCheck },
        { path: "/audit-matrices", label: t("nav:items.auditMatrices"), icon: Grid3X3, highlight: true },
        { path: "/client-documents", label: t("nav:items.clientDocuments"), icon: FolderOpen },
        { path: "/document-bridge", label: t("nav:items.documentBridge"), icon: FileCheck, highlight: true },
        { path: "/scar", label: t("nav:items.scarManagement"), icon: Truck, highlight: true },
        { path: "/supplier-scorecard", label: t("nav:items.supplierScorecard"), icon: BarChart3, highlight: true },
        { path: "/process-capture", label: t("nav:items.processCapture"), icon: Activity, highlight: true },
      ],
    },
    {
      id: "intelligence",
      label: t("nav:groups.intelligence"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/forms", label: t("nav:items.forms"), icon: Sparkles },
        { path: "/audit-workflow", label: t("nav:items.auditWorkflow"), icon: Workflow },
        { path: "/questionnaire", label: t("nav:items.questionnaire"), icon: FileQuestion },
        { path: "/gantt", label: t("nav:items.gantt"), icon: GanttChart },
        { path: "/knowledge", label: t("nav:items.knowledge"), icon: BookOpen },
      ],
    },
    {
      id: "improvement",
      label: t("nav:groups.improvement"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/improvement", label: t("nav:items.improvementPlans"), icon: Lightbulb },
        { path: "/sentinel", label: t("nav:items.sentinelAnalytics"), icon: Activity },
        { path: "/sdvr", label: t("nav:items.sdvrReports"), icon: Shield, highlight: true },
        { path: "/lifecycle", label: t("nav:items.componentLifecycle"), icon: Cpu, highlight: true },
        { path: "/capa-prevention", label: t("nav:items.capaPrevention"), icon: Gauge, highlight: true },
        { path: "/regulatory-radar", label: t("nav:items.regulatoryRadar"), icon: Globe, highlight: true },
      ],
    },
    {
      id: "auditiq",
      label: t("nav:groups.auditiq"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/audit-conductor", label: t("nav:items.auditConductor"), icon: Siren, highlight: true },
        { path: "/audit-warroom", label: t("nav:items.auditWarRoom"), icon: Siren },
        { path: "/scriber", label: t("nav:items.scriber"), icon: PenTool, highlight: true },
        { path: "/report-loader", label: t("nav:items.reportLoader"), icon: FileUp, highlight: true },
        { path: "/defense-scripts", label: t("nav:items.defenseScripts"), icon: Shield },
        { path: "/stat-explainer", label: t("nav:items.statExplainer"), icon: Calculator },
      ],
    },
    {
      id: "braindrain",
      label: t("nav:groups.braindrain"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/auditors-brain", label: t("nav:items.auditorsBrain"), icon: Brain, highlight: true },
        { path: "/knowledge-base", label: t("nav:items.knowledgeBase"), icon: Database },
        { path: "/calculators", label: t("nav:items.calculators"), icon: Calculator },
        { path: "/cases", label: t("nav:items.caseStudies"), icon: BookOpen },
        { path: "/qa", label: t("nav:items.qaEngine"), icon: HelpCircle },
        { path: "/qa-supervisor", label: "QA Supervisor", icon: Shield, highlight: true, roles: ["admin", "auditor"] },
        { path: "/labs", label: t("nav:items.labSimulations"), icon: FlaskConical, highlight: true },
      ],
    },
    {
      id: "publishing",
      label: t("nav:groups.publishing"),
      roles: ["admin"],
      items: [
        { path: "/publishing", label: t("nav:items.publishingPortal"), icon: Store, highlight: true },
      ],
    },
    {
      id: "governance",
      label: t("nav:groups.governance"),
      roles: ["admin", "auditor"],
      items: [
        { path: "/standards", label: t("nav:items.standardsPortal"), icon: BookOpen, highlight: true },
        { path: "/soa", label: t("nav:items.soaMatrix"), icon: ClipboardCheck, highlight: true },
        { path: "/governance", label: t("nav:items.governanceCenter"), icon: Shield, highlight: true },
        { path: "/governance-controls", label: t("nav:items.controlLibrary"), icon: Layers, highlight: true },
        { path: "/regional-obligations", label: t("nav:items.regionalObligations"), icon: Globe, highlight: true },
        { path: "/governance-assessments", label: t("nav:items.gapAssessments"), icon: ClipboardCheck, highlight: true },
        { path: "/remediation-tracker", label: t("nav:items.remediationTracker"), icon: ClipboardCheck, highlight: true },
        { path: "/governance-readiness", label: t("nav:items.governanceReadiness"), icon: ShieldCheck, highlight: true },
        { path: "/governance-library", label: "Governance Library", icon: Shield, highlight: true },
        { path: "/onboarding-checklist", label: t("nav:items.onboardingChecklist", { defaultValue: "Onboarding Checklist" }), icon: ClipboardCheck, highlight: true },
        { path: "/compliance-dashboard", label: t("nav:items.complianceDashboard"), icon: BarChart3 },
        { path: "/annex11-readiness", label: t("nav:items.annex11Readiness"), icon: ShieldAlert, highlight: true },
      ],
    },
    {
      id: "client",
      label: t("nav:groups.client"),
      items: [
        { path: "/client-portal", label: t("nav:items.clientPortal"), icon: PieChart },
        { path: "/client-onboarding", label: "My Onboarding", icon: ClipboardCheck, roles: ["client"] },
        { path: "/my-support", label: "My Support", icon: Headphones, highlight: true },
        { path: "/messages", label: t("nav:items.messages"), icon: Bell },
      ],
    },
    {
      id: "payments",
      label: t("nav:groups.payments", { defaultValue: "Payments" }),
      roles: ["admin"],
      items: [
        { path: "/payments", label: t("nav:items.paymentsPortal", { defaultValue: "C6Bank Payments" }), icon: Banknote, highlight: true },
      ],
    },
    {
      id: "platform",
      label: "Platform",
      roles: ["admin"],
      items: [
        { path: "/admin/platform-health", label: "Health Monitor", icon: HeartPulse, highlight: true },
        { path: "/admin/waf-monitor", label: "WAF & Security", icon: Shield, highlight: true },
        { path: "/admin/security-audit-logs", label: "Security Audit Logs", icon: ShieldCheck, highlight: true },
        { path: "/audit-explorer", label: "Audit-Trail Explorer", icon: Activity, highlight: true },
        { path: "/trial-funnel", label: "Trial Funnel", icon: TrendingDown, highlight: true },
        { path: "/admin/service-tokens", label: "Service Tokens", icon: Key, highlight: true },
        { path: "/admin/data-environment", label: "Data Environment", icon: Database, highlight: true },
        { path: "/esignatures", label: "E-Signatures", icon: PenTool, highlight: true },
        { path: "/settings/notifications", label: "Notification Prefs", icon: Bell },
      ],
    },
    {
      id: "management",
      label: t("nav:groups.management"),
      roles: ["admin"],
      items: [
        { path: "/training", label: t("nav:items.training"), icon: GraduationCap },
        { path: "/suppliers", label: t("nav:items.suppliers"), icon: Truck },
        { path: "/documents", label: t("nav:items.documents"), icon: FileText },
        { path: "/doc-control", label: "Document Control", icon: Shield, highlight: true },
        { path: "/geo-library", label: "Geography Library", icon: Globe, highlight: true },
        { path: "/svr", label: "Validation Reports", icon: FileCheck },
        { path: "/iso9001", label: "ISO 9001 Self-Assessment", icon: Shield },
        { path: "/reports", label: t("nav:items.reports"), icon: BarChart3 },
        { path: "/communications", label: t("nav:items.communications"), icon: Users },
        { path: "/contacts", label: t("nav:items.contacts"), icon: Users },
        { path: "/crm/organizations", label: t("nav:items.crmDashboard"), icon: Building2, highlight: true },
        { path: "/crm/support-tickets", label: "Support Tickets", icon: TicketCheck, highlight: true },
      ],
    },
  ];

  // Filter nav groups and items by user role
  const userRole = user?.role || "client";
  const visibleNavGroups = navGroups
    .filter(group => !group.roles || group.roles.includes(userRole))
    .map(group => ({
      ...group,
      items: group.items.filter(item => !item.roles || item.roles.includes(userRole))
    }));

  const adminItems = [
    { path: "/users", label: t("nav:items.users"), icon: Users },
    { path: "/projects", label: t("nav:items.projects"), icon: FolderKanban },
    { path: "/admin/access-codes", label: t("nav:items.accessCodes"), icon: BookOpen },
    { path: "/admin/qr-codes", label: t("nav:items.qrCodes"), icon: QrCode },
    { path: "/admin/brand-assets", label: "Brand Asset Hub", icon: Image },
    { path: "/audit-log", label: t("nav:items.auditLog"), icon: FileText },
    { path: "/notification-settings", label: t("nav:items.notifications"), icon: Cog },
    { path: "/notification-log", label: t("nav:items.deliveryLog"), icon: Bell },
    { path: "/enterprise-sso", label: t("nav:items.enterpriseSSO"), icon: ShieldCheck },
    { path: "/client-types", label: "Client Types", icon: Building2 },
    { path: "/compliance", label: t("nav:items.complianceNavigator"), icon: Shield },
    { path: "/compliance-dashboard", label: t("nav:items.complianceDashboard"), icon: BarChart3 },
    { path: "/connectors", label: t("nav:items.qmsConnectors"), icon: Network },
  ];

  // PR_RUN_039 — sidebar filter / expanded-state / persistence moved into
  // Sidebar.jsx. The unfiltered nav data still flows from here so the
  // CommandPalette consumes the same list shape as the sidebar.

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const getRoleBadge = (role) => {
    const styles = {
      admin: "badge-cyan",
      auditor: "badge-blue",
      client: "badge-green",
    };
    return (
      <span className={`badge ${styles[role] || "badge-gray"} text-xs`}>
        {t(`common:roles.${role}`, { defaultValue: role ? role.charAt(0).toUpperCase() + role.slice(1) : "User" })}
      </span>
    );
  };

  return (
    <CommandPaletteProvider>
      <div className="min-h-screen bg-[#111318]">
        {/* Sidebar (PR_RUN_035 extracted; PR_RUN_039 state-colocated) */}
        <Sidebar
          sidebarOpen={sidebarOpen}
          setSidebarOpen={setSidebarOpen}
          navGroups={visibleNavGroups}
          adminItems={adminItems}
          isAdmin={isAdmin}
          user={user}
          handleLogout={handleLogout}
          currentLang={currentLang}
          changeLang={changeLang}
          supportedLanguages={SUPPORTED_LANGUAGES}
        />

      {/* Main Content */}
      <main className="qms-main">
        {/* Topbar */}
        <header className="qms-topbar" data-testid="qms-topbar">
          <button
            className="md:hidden mr-4 text-[#8B95A5]"
            onClick={() => setSidebarOpen(!sidebarOpen)}
            data-testid="mobile-sidebar-btn"
          >
            {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
          </button>

          {/* Back + Home buttons */}
          {location.pathname !== "/dashboard" && (
            <div className="flex items-center gap-1 mr-3">
              <button
                onClick={() => navigate(-1)}
                className="p-1.5 rounded text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="topbar-back-btn"
                title={t("common:actions.back")}
              >
                <ArrowLeft size={18} />
              </button>
              <button
                onClick={() => navigate("/dashboard")}
                className="p-1.5 rounded text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.06)] transition-colors"
                data-testid="topbar-home-btn"
                title={t("nav:items.dashboard")}
              >
                <Home size={18} />
              </button>
            </div>
          )}

          <h1 className="font-['Syne'] text-lg font-semibold text-white">{title}</h1>

          <div className="ml-auto flex items-center gap-4">
            {/* PR_RUN_031 — discoverable ⌘K launcher (tester UX feedback);
                PR_RUN_035 — extracted into <CommandPaletteLauncher /> so it
                can consume the CommandPaletteContext via hooks (removed the
                prior `window.__openCommandPalette` global). */}
            <CommandPaletteLauncher />
            <NotificationBell />
            {/* Language Switcher — Topbar (WCAG Compliant) */}
            <div className="relative">
              <button
                ref={langBtnRef}
                id="language-picker-button"
                onClick={() => toggleLangDropdown(!langOpen)}
                onKeyDown={handleLangBtnKeyDown}
                aria-label={`Select Language (Current: ${currentLang.name})`}
                aria-haspopup="listbox"
                aria-expanded={langOpen}
                aria-controls="language-picker-dropdown"
                className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[#8B95A5] hover:text-white bg-[rgba(255,255,255,0.04)] hover:bg-[rgba(255,255,255,0.08)] transition-colors text-sm"
                data-testid="topbar-language-switcher-btn"
              >
                <Globe size={14} aria-hidden="true" />
                <span className="font-medium">{currentLang.shortName}</span>
                <ChevronDown size={12} aria-hidden="true" className={`transition-transform ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => toggleLangDropdown(false)} />
                  <ul
                    ref={langListRef}
                    id="language-picker-dropdown"
                    role="listbox"
                    aria-labelledby="language-picker-button"
                    onKeyDown={handleLangListKeyDown}
                    className="absolute right-0 top-full mt-2 w-48 bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-md shadow-lg z-50 list-none m-0 p-0"
                    data-testid="topbar-language-dropdown"
                  >
                    {SUPPORTED_LANGUAGES.map((lang, idx) => (
                      <li
                        key={lang.code}
                        ref={el => langOptionRefs.current[idx] = el}
                        role="option"
                        tabIndex={-1}
                        lang={lang.code}
                        aria-selected={i18n.language === lang.code}
                        onClick={() => changeLang(lang.code)}
                        className={`w-full px-3 py-2.5 flex items-center gap-2.5 text-sm transition-colors cursor-pointer first:rounded-t-md last:rounded-b-md outline-none focus:ring-1 focus:ring-[#38BDF8] focus:ring-inset ${
                          i18n.language === lang.code
                            ? "bg-[#36A9E1] bg-opacity-15 text-[#38BDF8]"
                            : "text-[#8B95A5] hover:text-white hover:bg-[rgba(255,255,255,0.04)]"
                        }`}
                        data-testid={`topbar-lang-${lang.code}`}
                      >
                        <span aria-hidden="true">{lang.flag}</span>
                        <span>{lang.name}</span>
                        {i18n.language === lang.code && (
                          <span className="ml-auto text-[#38BDF8] text-xs font-semibold" aria-hidden="true">&#10003;</span>
                        )}
                      </li>
                    ))}
                  </ul>
                </>
              )}
            </div>
            {getRoleBadge(user?.role)}
            <div className="relative">
              <button
                className="flex items-center gap-2 text-[#CBD5E1] hover:text-white transition-colors"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
                data-testid="user-menu-btn"
                aria-label="User menu"
                aria-expanded={userMenuOpen}
              >
                {user?.picture ? (
                  <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-[#2D313D] flex items-center justify-center">
                    <User size={14} />
                  </div>
                )}
                <ChevronDown size={14} />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-[#1F222D] border border-[rgba(255,255,255,0.07)] rounded-[3px] shadow-lg z-50">
                  <div className="p-3 border-b border-[rgba(255,255,255,0.07)]">
                    <p className="text-white text-sm font-medium truncate">{user?.name}</p>
                    <p className="text-[#8B95A5] text-xs truncate">{user?.email}</p>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full px-3 py-2 flex items-center gap-2 text-[#F87171] hover:bg-[rgba(224,85,85,0.1)] transition-colors text-sm"
                    data-testid="logout-btn"
                  >
                    <LogOut size={14} />
                    <span>{t("nav:logout")}</span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </header>

        {/* Content */}
        <div className="qms-content" data-testid="qms-content">
          {breadcrumbItems && <ContextBreadcrumb items={breadcrumbItems} />}
          {children}
        </div>
      </main>

      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div
          className="md:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* AI Note Taker - available across all modules */}
      <AiNoteTaker context={currentCtx.context} contextLabel={currentCtx.label} />

      {/* PR_RUN_031 — global ⌘K / Ctrl+K command palette (tester UX feedback);
          PR_RUN_035 — consumes CommandPaletteContext provided above. */}
      <CommandPalette navGroups={visibleNavGroups} />
      </div>
    </CommandPaletteProvider>
  );
};

export default QMSLayout;
